import { CartLinesUpdateEvent } from '@shopify/events';

const RECENT_KEY = 'c365:recently-viewed';
const RECENT_MAX = 4;
const MOBILE = window.matchMedia('(max-width: 749px)');

// Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
// Shopper-facing text in the visitor's language: window.C365_T is filled by sections/c365-product.liquid from
// locales (c365.js.product.*, labels as "label.<English value>"); without it (unit tests) the English below.
// `{name}` placeholders take `vars`.
const T = (k, f, vars) =>
  String(D(window.C365_T && window.C365_T[k]) || f).replace(/\{(\w+)\}/g, (m, n) => (vars && n in vars ? vars[n] : m));
/** English store data shown to shoppers (product type, frame, mat, shipping method…): its label, else as it is. */
const L = (s) => D(s && window.C365_T && window.C365_T[`label.${s}`]) || s;
/** Dates in the visitor's language (the page's locale), en-US without it. */
const LOCALE = () => (window.C365_T && window.C365_T.locale) || 'en-US';
/** Text with `{name}` slots filled by strings or DOM nodes, as a list for append()/replaceChildren(). */
const parts = (tpl, vars) =>
  tpl.split(/(\{\w+\})/).filter(Boolean).map((seg) => {
    const m = /^\{(\w+)\}$/.exec(seg);
    return m && m[1] in vars ? vars[m[1]] : seg;
  });

// Product types of the 365Canvas Options app (c365.options `types`) and their cart property "Product type".
const PTYPE_NAMES = { canvas: 'Canvas Print', framed: 'Framed Canvas', print: 'Framed Print' };
// Framed Print: cart property "Mat" (fulfilment reads these strings), the first is the default.
/** "16x20" (+ `add` inches each way) → "16″ × 20″"; anything unreadable as it is. */
function sizeText(size, add = 0) {
  const m = String(size || '').match(/(\d+(?:\.\d+)?)\D+?(\d+(?:\.\d+)?)/);
  return m ? `${Number(m[1]) + add}″ × ${Number(m[2]) + add}″` : String(size || '');
}

const MATS = ['White Mat', 'No Mat']; // "White Mat" was "White 2\" Mat" until 2026-10-19 (owner): the mat is 2″ only on the tight axis
/** The smallest of a list of "WxH" sizes, by area (the first one on a tie). */
const smallestSize = (sizes) => {
  const area = (s) => String(s).split(/[x×]/i).reduce((a, n) => a * (parseFloat(n) || 0), 1);
  return sizes.reduce((m, s) => (m === undefined || area(s) < area(m) ? s : m), undefined);
};
const PRINT_FRAMES = ['Black', 'White', 'Walnut', 'Natural', 'Rustic White', 'Gold', 'Silver'];

/**
 * The store's date today (checkout counts from it: the ship guard reads shop.localTime), as a local Date at midnight.
 * `offset` is the store's UTC offset ("-0700", from Liquid 'now' | date: '%z'); without it, the visitor's date.
 */
let storeOffset = null;
function storeToday() {
  const m = /^([+-])(\d\d)(\d\d)$/.exec(storeOffset || '');
  if (!m) return new Date();
  const mins = (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
  const s = new Date(Date.now() + mins * 60000);
  return new Date(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
}

/** Adds `n` business days (Mon–Fri) to the store's today; today is day 0 (owner, 2026-10-23). */
function addBusinessDays(n) {
  const d = storeToday();
  let c = 0;
  while (c < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) c++;
  }
  return d;
}

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * "Saved …" for a design in progress, from its last autosave, in the customer's own time (Site Rules §6).
 * Never rounded up: 59 min stays "59 min ago".
 *   under a minute → just now · under an hour → 5 min ago · same day → 2 hours ago · the day before →
 *   yesterday · 2–6 days → 3 days ago · longer → Oct 1 (with the year when it is another year)
 */
function savedWhen(at) {
  const now = new Date();
  const then = new Date(at);
  const minutes = Math.max(0, (now - then) / 60000);
  if (minutes < 1) return T('product.saved_just_now', 'Saved just now');
  if (minutes < 60) return T('product.saved_min_ago', 'Saved {n} min ago', { n: Math.floor(minutes) });
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(then)) / 864e5);
  if (days === 0) {
    const hours = Math.floor(minutes / 60);
    return hours === 1
      ? T('product.saved_hours_ago_one', 'Saved {n} hour ago', { n: hours })
      : T('product.saved_hours_ago_other', 'Saved {n} hours ago', { n: hours });
  }
  if (days === 1) return T('product.saved_yesterday', 'Saved yesterday');
  if (days < 7) return T('product.saved_days_ago', 'Saved {n} days ago', { n: days });
  const format = { month: 'short', day: 'numeric' };
  if (then.getFullYear() !== now.getFullYear()) format.year = 'numeric';
  return T('product.saved_on', 'Saved {date}', { date: then.toLocaleDateString(LOCALE(), format) });
}

function writeRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
}

class C365Product extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.data = JSON.parse(json.textContent);
    storeOffset = this.dataset.storeOffset || null;
    this.sizeIndex = Number(this.dataset.sizeIndex);
    this.frameIndex = Number(this.dataset.frameIndex);
    this.canvasValue = this.dataset.canvasValue;
    this.opts = this.dataset.mode === 'options' ? this.data.options : null;

    const current = this.data.variants.find((v) => v.id === this.data.selectedVariantId) || this.data.variants[0];
    this.size = this.sizeIndex >= 0 ? current.options[this.sizeIndex] : null;
    this.frame = this.frameIndex >= 0 ? current.options[this.frameIndex] : null;
    // Land on the smallest Canvas Print size unless a variant was linked to (owner 2026-10-23; was "Most popular").
    if (this.dataset.hasVariantParam !== 'true' && this.sizeIndex >= 0) {
      const canvas = this.data.variants.filter(
        (v) => v.available && (this.frameIndex < 0 || v.options[this.frameIndex] === this.canvasValue)
      );
      if (canvas.length) {
        this.size = smallestSize(canvas.map((v) => v.options[this.sizeIndex]));
        if (this.frameIndex >= 0) this.frame = this.canvasValue;
      }
    }
    this.lastFinish = this.frame && this.frame !== this.canvasValue ? this.frame : null;
    if (this.opts) this.initOptions(current);
    this.slide = 0;

    this.bindGallery();
    this.bindOptions();
    this.bindAccordions();
    this.bindSpecs();
    this.bindSizeGuide();
    this.bindCta();
    this.bindStickyBar();
    this.bindDesign();
    this.updateDelivery();
    this.applyLinkOptions();
    this.render();
    this.loadRelated();
    this.trackRecent();
  }

  $(sel) {
    return this.querySelector(sel);
  }

  $$(sel) {
    return Array.from(this.querySelectorAll(sel));
  }

  /** Shop-currency (USD) cents → the shopper's currency, as checkout converts them (option prices, shipping). */
  localCents(cents) {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return cents == null ? null : Math.round(cents * rate);
  }

  /** A USD amount from c365.options (e.g. 59.95) as the shopper pays it: the size chips' prices. */
  usdMoney(usd) {
    return this.money(this.localCents(Math.round(Number(usd) * 100)));
  }

  money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const whole = cents % 100 === 0;
    // t = thousands, dec = decimal mark, as Shopify's money filters write them
    const withDelim = (n, d, t, dec = t === '.' ? ',' : '.') => {
      const [i, f] = (n / 100).toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : '');
    };
    const d = whole ? 0 : 2;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelim(cents, 0, ',');
        case 'amount_with_comma_separator':
          return withDelim(cents, d, '.');
        case 'amount_no_decimals_with_comma_separator':
          return withDelim(cents, 0, '.');
        case 'amount_with_space_separator': // SEK, NOK, PLN…: 1 134,65
          return withDelim(cents, d, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withDelim(cents, 0, ' ');
        case 'amount_with_period_and_space_separator':
          return withDelim(cents, d, ' ', '.');
        case 'amount_with_apostrophe_separator': // CHF: 1'134.65
          return withDelim(cents, d, "'", '.');
        default:
          return withDelim(cents, d, ',');
      }
    });
  }

  /** Framed Canvas (floating frame). */
  get isFramed() {
    if (this.opts) return this.ptype === 'framed';
    return this.frame != null && this.frame !== this.canvasValue;
  }

  /** Framed Print (options mode only): paper print in a frame, with a Mat. */
  get isPrint() {
    return !!this.opts && this.ptype === 'print';
  }

  /** Either framed type: the line has a Frame. */
  get hasFrame() {
    return this.isFramed || this.isPrint;
  }

  get ptypeKey() {
    return this.opts ? this.ptype : this.isFramed ? 'framed' : 'canvas';
  }

  /** Frame colours of the selected type: c365.options `frames` (Framed Canvas) / `printFrames` (Framed Print). */
  get typeFrames() {
    if (!this.opts) return [];
    if (this.isPrint) return this.opts.printFrames?.length ? this.opts.printFrames : PRINT_FRAMES;
    return this.isFramed ? this.opts.frames || [] : [];
  }

  get variant() {
    if (this.opts) {
      return this.data.variants.find((v) => v.options.every((o, i) => this.vsel[i] === undefined || o === this.vsel[i]));
    }
    return this.data.variants.find(
      (v) =>
        (this.sizeIndex < 0 || v.options[this.sizeIndex] === this.size) &&
        (this.frameIndex < 0 || v.options[this.frameIndex] === this.frame)
    );
  }

  /* ---------- custom options (c365.options) ----------
   * Size / Product type / Frame come from the product metafield and go to the cart as line properties;
   * the 365Canvas Options app prices the line from them. Variants = the design's own attribute only. */
  initOptions(current) {
    this.popular = (this.dataset.popularSize || '').replace(/[×\s]/g, (c) => (c === '×' ? 'x' : '')).toLowerCase();
    this.ptype = 'canvas';
    this.frame = null;
    this.lastFinish = null;
    this.mat = MATS[0];
    this.vsel = [...current.options];
    this.size = null;
    this.fitSize();
  }

  /** Sizes the selected variant is sold in (c365.sizes; empty = every size of the product). */
  get allowedSizes() {
    // wall art: each product type has its own size list (owner, 2026-10-19), so only sizes priced for the type
    const rows = this.data.wall ? this.opts.sizes.filter((r) => Number(r[this.ptype]) > 0) : this.opts.sizes;
    const all = rows.map((r) => r.size);
    const own = this.variant?.sizes || [];
    return own.length ? all.filter((s) => own.includes(s)) : all;
  }

  /** Keep the size valid for the selected variant: current one if allowed, else the smallest (owner 2026-10-23). */
  fitSize() {
    const allowed = this.allowedSizes;
    if (allowed.includes(this.size)) return;
    this.size = smallestSize(allowed);
  }

  get sizeRow() {
    return this.opts?.sizes.find((r) => r.size === this.size);
  }

  /** Price in cents for the current option choice (options mode). */
  get optionPrice() {
    const p = Number(this.sizeRow?.[this.ptype]);
    return p > 0 ? Math.round(p * 100) : null;
  }

  get themeId() {
    return this.opts ? this.variant?.themeIds?.[this.size] || '' : '';
  }

  /** Line properties the pricing function and the order read. */
  get lineProperties() {
    if (!this.opts) return undefined;
    const props = {
      'Product type': PTYPE_NAMES[this.ptype],
      Size: this.size.replace('x', '×'),
      // width then height, e.g. 1612 = 16×12 landscape, 1216 = 12×16 vertical (for production + Personalize)
      _size_code: this.sizeRow?.code || this.size.replace('x', ''),
    };
    if (this.isPrint) props.Mat = this.mat;
    if (this.hasFrame) props.Frame = this.frame;
    if (this.themeId) props._theme_id = this.themeId;
    return props;
  }

  /* ---------- gallery ---------- */
  bindGallery() {
    this.slides = this.$$('[data-slide]');
    this.thumbs = this.$$('[data-thumb]');
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.showSlide(Number(t.dataset.thumb))));
    this.$$('[data-gallery-prev]').forEach((b) => b.addEventListener('click', () => this.showSlide(this.slide - 1)));
    this.$$('[data-gallery-next]').forEach((b) => b.addEventListener('click', () => this.showSlide(this.slide + 1)));

    // Mobile: the track is a swipeable scroll-snap row; keep the index in sync with swipes.
    this.track = this.$('[data-track]');
    this.track?.addEventListener(
      'scroll',
      () => {
        if (!MOBILE.matches) return;
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.slide) this.setSlide(i);
      },
      { passive: true }
    );

    const box = this.$('[data-lightbox]');
    const img = this.$('[data-lightbox-img]');
    this.$$('[data-zoom-open]').forEach((b) =>
      b.addEventListener('click', () => {
        const src = this.slides[this.slide]?.querySelector('img');
        if (!src) return;
        img.src = src.dataset.zoomSrc || src.currentSrc || src.src;
        box.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      })
    );
    const close = () => {
      box.hidden = true;
      document.documentElement.style.overflow = '';
    };
    box.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !box.hidden) close();
    });
  }

  showSlide(i) {
    const n = this.slides.length;
    if (!n) return;
    this.setSlide((i + n) % n);
    if (MOBILE.matches && this.track) {
      this.track.scrollTo({ left: this.slide * this.track.clientWidth, behavior: 'smooth' });
    }
  }

  setSlide(i) {
    this.slide = i;
    this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.slide));
    this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === this.slide));
    this.$$('[data-gallery-num]').forEach((num) => (num.textContent = String(this.slide + 1)));
  }

  /* ---------- options ---------- */
  bindOptions() {
    this.$$('[data-vopt]').forEach((b) =>
      b.addEventListener('click', () => {
        this.vsel[Number(b.dataset.vopt)] = b.dataset.value;
        this.fitSize();
        this.render();
      })
    );
    this.$$('[data-ptype]').forEach((b) =>
      b.addEventListener('click', () => {
        if (this.opts) {
          this.ptype = b.dataset.ptype;
          if (this.data.wall) this.fitSize();
          // keep the colour across Framed Canvas / Framed Print when the other type has it too
          const frames = this.typeFrames;
          if (this.ptype === 'canvas') this.frame = null;
          else if (!frames.includes(this.frame)) this.frame = frames.includes(this.lastFinish) ? this.lastFinish : frames[0];
        } else if (b.dataset.ptype === 'canvas') {
          this.frame = this.canvasValue;
        } else if (!this.isFramed) {
          const first = this.$('[data-frame]')?.dataset.frame;
          this.frame = this.lastFinish || first;
        }
        this.render();
      })
    );
    this.$$('[data-frame]').forEach((b) =>
      b.addEventListener('click', () => {
        this.frame = b.dataset.frame;
        this.lastFinish = this.frame;
        this.render();
      })
    );
    this.$$('[data-mat]').forEach((b) =>
      b.addEventListener('click', () => {
        this.mat = b.dataset.mat;
        this.render();
      })
    );
    this.$$('[data-size]').forEach((b) =>
      b.addEventListener('click', () => {
        this.size = b.dataset.size;
        this.render();
      })
    );
  }

  /**
   * Product type / Mat from the link (ads, emails): ?type=print&mat=no-mat lands on Framed Print, No Mat, with the
   * type's first frame colour and the usual size. Same as clicking the buttons, so a type not sold to this
   * customer (Framed Print: US only) or not priced falls back to Canvas Print.
   */
  applyLinkOptions(search = window.location.search) {
    const q = new URLSearchParams(search);
    const key = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
    const types = { canvas: 'canvas', canvasprint: 'canvas', framed: 'framed', framedcanvas: 'framed', print: 'print', framedprint: 'print' };
    const type = types[key(q.get('type'))];
    if (type) this.$(`[data-ptype="${type}"]`)?.click();
    const mat = MATS.find((m) => key(m) === key(q.get('mat')));
    if (mat) this.$(`[data-mat="${mat}"]`)?.click();
  }

  /**
   * Frame colours with limits (shop metafield c365.frame_rules, e.g. {"Gold": {"countries": ["US"],
   * "noSizes": ["40x60"]}}): Gold / Silver Framed Canvas is made by RV only, which ships in the US and is far
   * dearer than Jondo in the largest sizes. Size keys are short x long side. Framed Print (Jondo) isn't limited
   * by colour; the whole type is limited by `_types` (typeAllowed).
   */
  frameAllowed(name) {
    if (this.isPrint || String(name || '')[0] === '_') return true;
    const rule = this.data?.frameRules?.[name];
    if (!rule) return true;
    if (rule.countries && !rule.countries.includes(this.data.country)) return false;
    const [a, b] = String(this.size || '').split('x').map(Number).sort((x, y) => x - y);
    return !(rule.noSizes || []).includes(`${a}x${b}`);
  }

  /** Product type sold in the customer's country (c365.frame_rules `_types`, e.g. Framed Print: US only). */
  typeAllowed(type) {
    const countries = this.data?.frameRules?._types?.[type]?.countries;
    return !countries || countries.includes(this.data.country);
  }

  /** Swatch shown: allowed by c365.frame_rules and, with options, one of the selected type's colours. */
  frameOffered(name) {
    return this.frameAllowed(name) && (!this.opts || !this.hasFrame || this.typeFrames.includes(name));
  }

  render() {
    if (this.opts && this.hasFrame && !this.frameOffered(this.frame)) {
      const frames = this.typeFrames;
      this.frame = [this.lastFinish, ...frames].find((f) => f && this.frameOffered(f)) || frames[0];
    }
    this.$$('[data-frame]').forEach((b) => (b.hidden = !this.frameOffered(b.dataset.frame)));
    // swatches in the selected type's own order (Framed Print lists Rustic White before Gold / Silver)
    if (this.opts) this.$$('.c365-finish[data-frame]').forEach((b) => (b.style.order = String(this.typeFrames.indexOf(b.dataset.frame))));
    const ptypeName = L(PTYPE_NAMES[this.ptypeKey]);
    this.$$('[data-ptype-name]').forEach((el) => (el.textContent = ptypeName));
    this.$$('[data-ptype]').forEach((b) => b.classList.toggle('is-active', b.dataset.ptype === this.ptypeKey));
    const finish = this.$('[data-finish-group]');
    if (finish) finish.hidden = !this.hasFrame;
    const mats = this.$('[data-mat-group]');
    if (mats) mats.hidden = !this.isPrint;
    this.$$('[data-mat]').forEach((b) => b.classList.toggle('is-active', b.dataset.mat === this.mat));
    this.$$('[data-mat-name]').forEach((el) => (el.textContent = L((this.mat || '').replace('"', '″'))));
    this.$$('[data-frame]').forEach((b) => b.classList.toggle('is-active', b.dataset.frame === this.frame));
    this.$$('[data-frame-name]').forEach((el) => (el.textContent = L(this.frame) || ''));
    this.$$('[data-size]').forEach((b) => b.classList.toggle('is-active', b.dataset.size === this.size));
    this.$$('[data-size-name]').forEach((el) => (el.textContent = sizeText(this.size)));
    // GreatBigCanvas-style label (owner, 2026-10-19): the chips are the image size; the finished size adds the frame
    this.$$('[data-finished-size]').forEach((el) => (el.textContent = sizeText(this.size, this.frameAdds)));
    if (this.opts) {
      const allowed = this.allowedSizes;
      this.$$('[data-size]').forEach((b) => (b.hidden = !allowed.includes(b.dataset.size)));
      this.$$('[data-sg-size]').forEach((c) => (c.hidden = !allowed.includes(c.dataset.sgSize)));
      // a framed type only where the size has a price for it
      const priced = (type) =>
        this.data.wall ? this.opts.sizes.some((r) => Number(r[type]) > 0) : Number(this.sizeRow?.[type]) > 0;
      ['framed', 'print'].forEach((type) => this.$$(`[data-ptype="${type}"]`).forEach((b) => (b.disabled = !priced(type))));
      // a type not sold in this country isn't shown at all (Framed Print: US only)
      this.$$('[data-ptype]').forEach((b) => (b.hidden = !this.typeAllowed(b.dataset.ptype)));
      this.$$('.c365-ptypes').forEach((el) =>
        el.classList.toggle('c365-ptypes--3', this.$$('[data-ptype]').filter((b) => !b.hidden).length > 2)
      );
      if (this.ptype !== 'canvas' && (!priced(this.ptype) || !this.typeAllowed(this.ptype))) {
        return this.$('[data-ptype="canvas"]')?.click();
      }
      this.$$('[data-vopt]').forEach((b) =>
        b.classList.toggle('is-active', this.vsel[Number(b.dataset.vopt)] === b.dataset.value)
      );
      this.$$('[data-vopt-name]').forEach((el) => (el.textContent = L(this.vsel[Number(el.dataset.voptName)]) || ''));
    }

    const v = this.variant;
    // option prices are USD (c365.options); variant prices already come in the shopper's currency
    const price = this.opts ? this.localCents(this.optionPrice) : v?.price;
    if (v && price != null) {
      this.$$('[data-price]').forEach((el) => (el.textContent = this.money(price)));
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(v.id));
      window.history.replaceState({}, '', url.toString());
    }
    this.renderCta();
    this.$('[data-added]').hidden = true;
    this.$('[data-error]').hidden = true;
    this.renderSpecs();
    this.renderSaved();
    this.renderBadges();
    this.renderShipping();
    this.renderDelivery();
    this.renderMockup();
    // Room Art (assets/c365-room-art.js) and other listeners redraw from size / type / frame / variant.
    this.dispatchEvent(new CustomEvent('c365:change'));
  }

  /**
   * Main button: Personalize (or Add to cart for a static design). With a design in progress for this
   * selection it reads Continue personalizing; with a saved one, Add to cart.
   */
  renderCta() {
    const v = this.variant;
    const ok = !!v && (this.opts ? this.optionPrice : v.price) != null;
    const state = this.design?.state;
    // Add to cart was pressed while the design was still being saved: it goes in as soon as that is done
    const waiting = !!this.addWhenSaved && !!this.design?.saving;
    let label = T('product.unavailable', 'Unavailable');
    if (ok && !v.available) label = T('product.sold_out', 'Sold out');
    else if (ok && waiting) label = T('product.saving_design', 'Saving your design…');
    else if (ok && state === 'draft') label = T('product.continue_personalizing', 'Continue personalizing');
    else if (ok) label = state === 'ready' || (this.opts && !this.themeId) ? this.data.addLabel : this.data.ctaLabel;
    this.$$('[data-cta]').forEach((b) => (b.disabled = !ok || !v.available || waiting));
    this.$$('[data-cta-label]').forEach((el) => (el.textContent = label));
    // icon: pen = Continue personalizing, cart = Add to cart, wand = Personalize; the running border is for
    // a saved design that is ready to add
    const adding = ok && v.available && (state === 'ready' || (this.opts && !this.themeId));
    const icon = ok && v.available && state === 'draft' ? 'pen' : adding ? 'cart' : 'wand';
    this.$$('[data-cta-icon]').forEach((el) => el.toggleAttribute('hidden', el.dataset.ctaIcon !== icon));
    this.$$('[data-cta-ring]').forEach((el) => el.toggleAttribute('hidden', !(ok && v.available && state === 'ready' && !waiting)));
  }

  /* ---------- customer's design ---------- */
  // `c365:design` {state, key, image, at, properties, saving, picture} from the Personalize adapter
  // (assets/c365-personalize.js): the customer's design for the current selection.
  //   draft — left the tool with unsaved changes: card "Your design is in progress", Continue personalizing
  //   ready — saved in the tool: card "Your design is ready", its picture as the main gallery image,
  //           Edit design, Add to cart (the line carries `properties`). While `saving` the tool is still
  //           uploading in the background: the picture is the one taken from the canvas, there are no
  //           properties yet, and Add to cart waits for them.
  //   none  — nothing extra
  // Either card comes with "or start a new design", which asks before it discards.
  bindDesign() {
    this.design = null;
    document.addEventListener('c365:design', (e) => {
      const was = this.design?.state;
      this.design = e.detail?.state && e.detail.state !== 'none' ? e.detail : null;
      if (this.leaving) return; // on its way to the cart: the page stays as the customer left it
      const add = this.addWhenSaved && !this.design?.saving;
      if (add) this.addWhenSaved = false;
      this.renderCta();
      this.renderSaved();
      this.renderMockup();
      if (this.design?.state === 'ready' && was !== 'ready') this.showSlide(0);
      if (add && this.design?.state === 'ready') this.addDesign();
    });
    // every design the customer has on this product, by template: {theme id: 'draft' | 'ready'}
    this.savedStates = {};
    document.addEventListener('c365:designs', (e) => {
      this.savedStates = e.detail?.states || {};
      if (!this.leaving) this.renderBadges();
    });
    const confirm = (open) => {
      this.$('[data-new]').hidden = open || !this.design;
      this.$('[data-discard]').hidden = !open;
    };
    this.$('[data-edit]')?.addEventListener('click', () => this.openEditor());
    this.$('[data-new]')?.addEventListener('click', () => confirm(true));
    this.$('[data-discard-no]')?.addEventListener('click', () => confirm(false));
    this.$('[data-discard-yes]')?.addEventListener('click', () => {
      if (this.design) this.dispatchEvent(new CustomEvent('c365:design-discard', { bubbles: true, detail: this.design }));
    });
  }

  /** The "Your design is in progress / is ready" card above the main button. */
  renderSaved() {
    const card = this.$('[data-saved]');
    if (!card) return;
    const design = this.design;
    card.hidden = !design;
    this.$('[data-new]').hidden = !design || !!design.saving;
    this.$('[data-discard]').hidden = true;
    if (!design) return;
    const ready = design.state === 'ready';
    const size = `${(this.size || '').replace('x', '×')}″`;
    const finish = this.isPrint
      ? T('product.saved_print_finish', '{frame} Frame · {mat}', { frame: L(this.frame), mat: L(this.mat.replace('"', '″')) })
      : this.isFramed ? T('product.saved_framed_finish', '{frame} Floating Frame', { frame: L(this.frame) }) : L('Canvas Print');
    this.$('[data-saved-title]').textContent = ready
      ? T('product.design_ready', 'Your design is ready')
      : T('product.design_in_progress', 'Your design is in progress');
    this.$('[data-saved-badge]').textContent = design.saving
      ? T('product.badge_saving', 'Saving…')
      : ready ? T('product.badge_saved', 'Saved') : T('product.badge_not_finished', 'Not finished');
    this.$('[data-saved-sub]').textContent = ready ? `${size} · ${finish}` : `${size} · ${savedWhen(design.at)}`;
    this.$('[data-edit]').hidden = !ready || !!design.saving;
    this.$$('[data-saved-icon]').forEach((el) => el.toggleAttribute('hidden', el.dataset.savedIcon !== design.state));
  }

  /** "Draft" / "Saved" on every size tile of the selected design that holds one of the customer's designs. */
  renderBadges() {
    const ids = this.variant?.themeIds || {};
    this.$$('[data-size]').forEach((tile) => {
      const state = this.savedStates?.[ids[tile.dataset.size]];
      let badge = tile.querySelector('.c365-size__saved');
      if (!state) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'c365-size__saved';
        badge.innerHTML =
          '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg><span></span>';
        tile.append(badge);
      }
      const ready = state === 'ready';
      badge.lastElementChild.textContent = ready ? T('product.badge_saved', 'Saved') : T('product.badge_draft', 'Draft');
      badge.title = ready
        ? T('product.size_has_saved', 'Your saved design is on this size')
        : T('product.size_has_draft', 'Your design in progress is on this size');
    });
  }

  /* ---------- finish mockups ---------- */
  // Slide 0 shows the mockup for the selected design + finish (tools/mockups/publish.py → c365.mockups).
  // Framed Print has no stored mockups (7 frames × 2 mats per design would pass the 250-media cap): it is drawn
  // in the browser (assets/c365-mockup.js) from the design's flat front image at the selected size, Mat and
  // Frame; until that is ready, or when there is no flat image, the floating-frame mockup in the same colour.
  get finishKey() {
    if (!this.hasFrame) return 'wrap';
    const v = (this.frame || '').toLowerCase();
    if (v.includes('white')) return 'white';
    if (v.includes('walnut')) return 'walnut';
    if (v.includes('natural') || v.includes('oak')) return 'oak';
    if (v.includes('gold')) return 'gold';
    if (v.includes('silver')) return 'silver';
    return 'black';
  }

  /** Framed Print finish for assets/c365-mockup.js (fp-<frame>-<mat|nomat>), null for the other types. */
  get printFinishKey() {
    if (!this.isPrint) return null;
    const v = (this.frame || '').toLowerCase();
    const frame = ['rustic', 'white', 'walnut', 'natural', 'gold', 'silver'].find((k) => v.includes(k)) || 'black';
    return `fp-${frame}-${this.mat === 'No Mat' ? 'nomat' : 'mat'}`;
  }

  /** The selected design's flat front image (variant c365.room_images): exact size, then same ratio, then any. */
  get flatArt() {
    const imgs = this.variant?.roomImages || {};
    if (imgs[this.size]) return imgs[this.size];
    const [w, h] = String(this.size || '').split('x').map(Number);
    const same = Object.keys(imgs).find((k) => {
      const [a, b] = k.split('x').map(Number);
      return a && b && w && h && Math.abs(a / b - w / h) < 0.02;
    });
    return imgs[same] || imgs['*'] || Object.values(imgs)[0] || null;
  }

  renderMockup() {
    const m = this.data.mockups;
    let media;
    if (m) {
      const byFinish = m.v[String(this.variant?.id)] || Object.values(m.v)[0];
      media = byFinish && m.media[byFinish[this.finishKey] || byFinish.wrap];
    } else if (this.data.variantMedia) {
      // no mockups: the selected design's own image (other designs' images aren't in the gallery)
      media = this.data.variantMedia[String(this.variant?.id)];
    }
    const img = this.$('[data-mockup-img="true"]');
    if (!img) return;
    const thumb = this.$('[data-mockup-thumb="true"]');
    // what the page was rendered with, for products that have nothing to swap back to
    this.stockMockup ||= {
      src: img.currentSrc || img.src,
      srcset: img.getAttribute('srcset') || '',
      alt: img.alt,
      zoom: img.dataset.zoomSrc,
      thumb: thumb?.currentSrc || thumb?.src,
    };
    if (!media && img.dataset.mockupSrc) media = this.stockMockup;

    const design = this.design?.state === 'ready' ? this.design.picture || this.design.image : null;
    const print = this.printFinishKey;
    // wall art: the artwork's flat image drawn as the selected finish (Gallery Wrap / frame) at the selected size
    const art = design || (print ? this.flatArt : null) || (this.data.wall ? this.data.flatImage : null);
    if (!art) {
      this.mockupJob = null;
      this.setMockup(media, false);
      return;
    }
    // The customer's finished design takes the stock mockup's place, drawn as the same 3/4 view in the
    // selected finish (assets/c365-mockup.js); a Framed Print is drawn that way from the design's flat image too.
    // What is on screen stays until that picture is ready; if it cannot be drawn, the customer's flat design
    // is shown instead (a stock Framed Print keeps the floating-frame mockup).
    const finish = print || this.finishKey;
    const job = (this.mockupJob = `${finish}|${this.size}|${art}`);
    const yours = !!design;
    const own = (src) => ({ src, srcset: '', alt: yours ? T('product.your_design', 'Your design') : media?.alt || this.stockMockup.alt, zoom: src, thumb: src });
    if (!yours) this.setMockup(null, false); // the badge goes now; the picture when the new one is ready
    import(this.data.mockupJs)
      .then(({ mockup }) => mockup(art, finish, MOBILE.matches ? 1200 : 1600, this.size, design ? this.design.image : null, this.themeId || null))
      .catch((err) => {
        console.warn('[c365] design mockup', err);
        return design;
      })
      .then((src) => {
        if (this.mockupJob !== job) return;
        if (src) this.setMockup(own(src), yours);
        else this.setMockup(media, false);
      });
  }

  /** Slide 0 + its thumb; `yours` marks the customer's own design (badge "Your design"). */
  setMockup(media, yours) {
    const img = this.$('[data-mockup-img="true"]');
    const thumb = this.$('[data-mockup-thumb="true"]');
    const slide = img.closest('[data-slide]');
    let badge = slide?.querySelector('[data-your-design]');
    if (yours && slide && !badge) {
      badge = document.createElement('span');
      badge.className = 'c365-your-design';
      badge.dataset.yourDesign = '';
      badge.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';
      badge.append(T('product.your_design', 'Your design'));
      slide.append(badge);
    }
    if (badge) badge.hidden = !yours;
    slide?.classList.toggle('is-your-design', yours);
    thumb?.closest('[data-thumb]')?.classList.toggle('is-your-design', yours);
    if (!media || img.dataset.mockupSrc === media.src) return;
    img.dataset.mockupSrc = media.src;
    if (media.srcset) img.srcset = media.srcset;
    else img.removeAttribute('srcset');
    img.src = media.src;
    img.alt = media.alt;
    img.dataset.zoomSrc = media.zoom;
    if (thumb) {
      thumb.removeAttribute('srcset');
      thumb.src = media.thumb;
    }
  }

  /* ---------- specs ---------- */
  bindSpecs() {
    const specs = this.$('[data-specs]');
    const btn = this.$('[data-specs-toggle]');
    btn.addEventListener('click', () => {
      const open = specs.classList.toggle('is-open');
      btn.textContent = open ? T('product.show_less', 'Show less') : T('product.show_more', 'Show more');
      btn.setAttribute('aria-expanded', String(open));
    });
  }

  /** Inches the frame adds to the image size each way: Canvas Print 0, Framed Canvas 1, Framed Print 2. */
  get frameAdds() {
    return this.isPrint ? 2 : this.isFramed ? 1 : 0;
  }

  renderSpecs() {
    const s = this.data.specs;
    const [w, h] = (this.size || '').split(/[×x]/).map((x) => Number(x.trim()));
    const fr = this.isFramed;
    const pr = this.isPrint;
    const imageSize = T('product.spec_image_size', 'Image size');
    const finishedSize = T('product.spec_finished_size', 'Finished size');
    const rows = [[L('Product type'), L(PTYPE_NAMES[this.ptypeKey])]];
    if (pr) {
      rows.push(
        [L('Frame'), T('product.spec_frame_value', '{frame} frame', { frame: L(this.frame) })],
        [L('Mat'), L(this.mat.replace('"', '″'))]
      );
      if (w && h) {
        // owner, 2026-10-19: Image size = the size chosen (the print); Finished = + 2″ (Jondo: 12×18 → 14×20)
        rows.push([imageSize, sizeText(this.size)]);
        rows.push([finishedSize, sizeText(this.size, this.frameAdds)]);
      }
    } else {
      const floating = T('product.spec_floating_frame_value', '{frame} floating frame', { frame: L(this.frame) });
      rows.push([
        T('product.spec_finish', 'Finish'),
        fr ? (this.opts ? floating : L(this.frame)) : T('product.spec_gallery_wrap', 'Gallery wrap'),
      ]);
      if (w && h) {
        rows.push([imageSize, `${w}″ × ${h}″`]);
        rows.push([finishedSize, sizeText(this.size, this.frameAdds)]);
      }
    }
    rows.push(
      [T('product.spec_depth', 'Depth'), pr ? s.depthPrint : fr ? s.depthFramed : s.depthCanvas],
      // canvas wording (material, hanging) doesn't fit the paper print
      [T('product.spec_material', 'Material'), pr ? '' : s.material],
      [T('product.spec_inks', 'Inks'), s.inks],
      [T('product.spec_hanging', 'Hanging'), pr ? '' : s.hanging]
    );
    const visible = rows.filter(([, v]) => v);
    this.$$('[data-spec-rows]').forEach((grid) =>
      grid.replaceChildren(
        ...visible.map(([k, v]) => {
          const row = document.createElement('div');
          row.className = 'c365-specs__row';
          const a = document.createElement('span');
          a.textContent = k;
          const b = document.createElement('span');
          b.textContent = v;
          row.append(a, b);
          return row;
        })
      )
    );
    const bullets = ((pr ? s.bulletsPrint : fr ? s.bulletsFramed : s.bulletsCanvas) || []).map((t) => t.trim()).filter(Boolean);
    this.$$('[data-spec-bullets]').forEach((ul) =>
      ul.replaceChildren(
        ...bullets.map((t) => {
          const li = document.createElement('li');
          li.textContent = t;
          return li;
        })
      )
    );
  }

  /* ---------- accordions ---------- */
  bindAccordions() {
    const details = this.$$('[data-detail]');
    details.forEach((d) => {
      d.querySelector('button').addEventListener('click', () => {
        const willOpen = d.querySelector('button').getAttribute('aria-expanded') !== 'true';
        details.forEach((o) => {
          const open = o === d && willOpen;
          o.querySelector('button').setAttribute('aria-expanded', String(open));
          o.querySelector('.c365-detail__icon').textContent = open ? '−' : '+';
          o.lastElementChild.hidden = !open;
        });
      });
    });
    this.$('[data-dest]')?.addEventListener('change', () => this.updateDelivery());

    const faqs = this.$$('[data-faq]');
    faqs.forEach((f) => {
      f.querySelector('button').addEventListener('click', () => {
        const willOpen = !f.classList.contains('is-open');
        faqs.forEach((o) => {
          const open = o === f && willOpen;
          o.classList.toggle('is-open', open);
          o.querySelector('button').setAttribute('aria-expanded', String(open));
          o.querySelector('.c365-faq__a').hidden = !open;
        });
      });
    });
  }

  /**
   * Size class of the chosen item, as Jondo prices shipping (365canvas options shared/shipping.js sizeClass):
   * outer inches 2 × (w + h), + 2 for a Framed Canvas.
   */
  get shipClass() {
    const [w, h] = String(this.size || '').split(/[x×]/).map(Number);
    if (!(w > 0 && h > 0)) return 'canvas';
    const outer = 2 * (w + h) + (this.opts && this.ptype === 'framed' ? 2 : 0);
    return outer > 220 ? 'oversized' : outer > 170 ? 'large' : outer > 132 ? 'medium' : 'canvas';
  }

  /**
   * Delivery date per shipping method for the Deliver-to country and the chosen size (shop c365.shipping rates:
   * days = production + Jondo transit, business days, today = day 0). Economy / Standard are estimates, Express /
   * Priority guaranteed — the same dates checkout puts in the rate titles (options repo shared/shipping.js).
   */
  renderDelivery() {
    const opt = this.$('[data-dest]')?.selectedOptions[0];
    const list = this.$('[data-ship-methods]');
    const line = this.$('[data-deliver-line]');
    let rates = null;
    try {
      rates = opt?.dataset.rates ? JSON.parse(opt.dataset.rates) : null;
    } catch {
      rates = null;
    }
    if (!rates) return false;
    const order = ['canvas', 'medium', 'large', 'oversized'];
    const cls = this.shipClass;
    const rule = this.data?.frameRules?.[this.frame];
    const noPriority = this.opts && this.ptype === 'framed' && !!rule && String(this.frame)[0] !== '_';
    const rows = rates
      .map((r) => {
        const price = typeof r.price === 'number'
          ? (r.maxClass && order.indexOf(cls) > order.indexOf(r.maxClass) ? null : r.price)
          : r.price?.[cls];
        return price == null || (r.method === 'priority' && noPriority) ? null : { ...r, usd: Number(price) };
      })
      .filter(Boolean);
    const short = (d) => d.toLocaleDateString(LOCALE(), { month: 'short', day: 'numeric' });
    const long = (d) => d.toLocaleDateString(LOCALE(), { weekday: 'short', month: 'short', day: 'numeric' });
    const guaranteed = (r) => r.method === 'express' || r.method === 'priority';
    const when = (r) =>
      guaranteed(r)
        ? T('product.guaranteed_by', 'Guaranteed by {date}', { date: long(addBusinessDays(r.days[1])) })
        : `${short(addBusinessDays(r.days[0]))} – ${short(addBusinessDays(r.days[1]))}`;
    const name = (m) => L(m[0].toUpperCase() + m.slice(1));
    if (list) {
      list.replaceChildren(
        ...rows.map((r) => {
          const li = document.createElement('li');
          li.className = guaranteed(r) ? 'is-guaranteed' : '';
          for (const text of [name(r.method), r.usd === 0 ? T('product.ship_free', 'Free') : this.money(this.localCents(Math.round(r.usd * 100))), when(r)]) {
            const s = document.createElement('span');
            s.textContent = text;
            li.append(s);
          }
          return li;
        })
      );
      list.hidden = rows.length === 0;
    }
    const etaRow = this.$('[data-eta-row]');
    if (etaRow) etaRow.hidden = true;
    if (line) {
      const eco = rows.find((r) => r.method === 'economy');
      const fast = rows.find((r) => r.method === 'priority') || rows.find((r) => r.method === 'express');
      line.parentElement.hidden = !eco;
      if (eco) {
        const b = (t) => {
          const el = document.createElement('b');
          el.textContent = t;
          return el;
        };
        const out = parts(T('product.deliver_arrives', 'Order now, arrives {dates}'), {
          dates: b(`${short(addBusinessDays(eco.days[0]))} – ${short(addBusinessDays(eco.days[1]))}`),
        });
        if (fast) {
          out.push(' · ', ...parts(T('product.deliver_guaranteed', 'Guaranteed by {date} with {method}'), {
            date: b(long(addBusinessDays(fast.days[1]))),
            method: name(fast.method),
          }));
        }
        line.replaceChildren(...out);
      }
    }
    return true;
  }

  /** Shipping pill next to the price: Free / + price / not shipped, for the Deliver-to country and the chosen size. */
  renderShipping() {
    const pill = this.$('[data-ship-pill]');
    const label = this.$('[data-ship-label]');
    const opt = this.$('[data-dest]')?.selectedOptions[0];
    if (!pill || !label || !opt?.dataset.economy) return;
    let prices = {};
    try {
      prices = JSON.parse(opt.dataset.economy);
    } catch {
      return;
    }
    const usd = prices[this.shipClass];
    label.dataset.free ??= label.textContent;
    pill.hidden = false;
    if (usd == null) label.textContent = T('product.ship_not_this_size', "Doesn't ship to {country} in this size", { country: opt.textContent.trim() });
    else if (Number(usd) === 0) label.textContent = label.dataset.free;
    else label.textContent = T('product.ship_price', '+{price} shipping', { price: this.money(this.localCents(Math.round(Number(usd) * 100))) });
  }

  updateDelivery() {
    const sel = this.$('[data-dest]');
    const opt = sel ? sel.selectedOptions[0] : null;
    const origin = this.$('[data-origin]');
    if (origin && opt?.dataset.origin) origin.textContent = opt.dataset.origin;
    this.renderShipping();
    if (this.renderDelivery()) return;
    // no shipping data (metafield missing): Economy days of the Deliver-to option
    const min = Number(opt?.dataset.min || 4);
    const max = Number(opt?.dataset.max || 9);
    const short = (d) => d.toLocaleDateString(LOCALE(), { month: 'short', day: 'numeric' });
    const eta = this.$('[data-eta]');
    if (eta) eta.textContent = `${short(addBusinessDays(min))} - ${short(addBusinessDays(max))}`;
    const by = this.$('[data-deliver-by]');
    if (by) by.textContent = addBusinessDays(max).toLocaleDateString(LOCALE(), {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  /* ---------- size guide ---------- */
  bindSizeGuide() {
    const sg = this.$('[data-sg]');
    if (!sg) return;
    const open = () => {
      sg.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    };
    const close = () => {
      sg.hidden = true;
      document.documentElement.style.overflow = '';
    };
    this.$$('[data-sg-open]').forEach((b) => b.addEventListener('click', open)); // one per type block, not only the first
    this.$('[data-sg-close]').addEventListener('click', close);
    sg.addEventListener('click', (e) => {
      if (e.target === sg) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !sg.hidden) close();
    });
    const sub = this.$('[data-sg-sub]');
    this.$$('[data-sg-tab]').forEach((t) =>
      t.addEventListener('click', () => {
        const tab = t.dataset.sgTab;
        this.$$('[data-sg-tab]').forEach((o) => o.classList.toggle('is-active', o === t));
        this.$$('[data-sg-pane]').forEach((p) => (p.hidden = p.dataset.sgPane !== tab));
        sub.textContent =
          tab === 'hand'
            ? T('product.sg_sub_hand', 'Each size held by a 5′6″ person, to scale')
            : T('product.sg_sub_wall', 'Every size shown to scale above a standard 84″ sofa');
      })
    );
  }


  /* ---------- add to cart ---------- */
  bindCta() {
    const onClick = async () => {
      const v = this.variant;
      if (!v || !v.available) return;
      // a saved design for this selection. Still uploading: the button waits, then adds by itself
      const design = this.design;
      if (design?.state === 'ready') {
        if (design.saving) {
          this.addWhenSaved = true;
          this.renderCta();
        } else this.addDesign();
        return;
      }
      // Personalize / Continue personalizing. Without a Personalize adapter — or for static designs — the
      // line is added straight away.
      if (this.openEditor()) return;
      if (await this.addToCart()) this.toCart();
    };
    this.$$('[data-cta]').forEach((b) => b.addEventListener('click', onClick));
    // back from the cart with the page kept as it was (back/forward cache): bring it up to date
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted || !this.leaving) return;
      this.leaving = false;
      this.$('[data-added]').hidden = true;
      this.renderCta();
      this.renderSaved();
      this.renderBadges();
      this.renderMockup();
    });
  }

  /** The saved design goes into the cart with its own properties; then the cart is shown. */
  async addDesign() {
    const design = this.design;
    if (design?.state !== 'ready' || !design.properties) return;
    if (!(await this.addToCart({ ...this.lineProperties, ...design.properties }))) return;
    // going to the cart page: the page stays as the customer left it. With the drawer it is new again behind it
    if (!this.cartDrawer) this.leaving = true;
    this.dispatchEvent(new CustomEvent('c365:design-added', { bubbles: true, detail: design }));
    this.toCart();
  }

  /** The cart drawer every page but the cart page has (sections/c365-cart-drawer.liquid), once it is ready. */
  get cartDrawer() {
    const drawer = document.querySelector('c365-cart-drawer');
    return typeof drawer?.open === 'function' ? drawer : null;
  }

  /** Added: the cart drawer opens over the page. Without it, straight on to the cart page. */
  toCart() {
    const drawer = this.cartDrawer;
    if (drawer) {
      this.$('[data-added]').hidden = true;
      drawer.open();
      return;
    }
    this.leaving = true;
    this.$$('[data-cta]').forEach((b) => (b.disabled = true));
    window.location.assign(this.dataset.cartUrl);
  }

  /**
   * Asks the Personalize adapter (assets/c365-personalize.js) to open the editor for the selected design and
   * size; true when it took the request. What the customer leaves there comes back as `c365:design`.
   */
  openEditor() {
    const v = this.variant;
    if (!v || !v.available || !this.themeId) return false;
    const ev = new CustomEvent('c365:personalize', {
      bubbles: true,
      cancelable: true,
      detail: { variantId: v.id, themeId: this.themeId, properties: this.lineProperties, productId: this.dataset.productId },
    });
    return !this.dispatchEvent(ev);
  }

  /** Adds the selected variant with the given line properties; resolves to whether it went in. */
  async addToCart(properties = this.lineProperties, quantity = 1) {
    const v = this.variant;
    if (!v || !v.available) return false;
    const ctas = this.$$('[data-cta]');
    ctas.forEach((b) => (b.disabled = true));
    const added = this.$('[data-added]');
    const error = this.$('[data-error]');
    added.hidden = true;
    error.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(v.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity, properties }] }),
      });
      const body = await res.json();
      const cart = await fetch(`${this.dataset.cartUrl}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: !res.ok, items: cart.items, itemCount: quantity, productId: this.dataset.productId },
      });
      if (!res.ok) throw new Error(body.description || body.message || T('product.add_error', 'Could not add to cart'));
      added.hidden = false;
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => (added.hidden = true), 4000);
      return true;
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      deferred.reject?.(err);
      return false;
    } finally {
      ctas.forEach((b) => (b.disabled = false));
    }
  }

  /* ---------- mobile sticky bar ---------- */
  bindStickyBar() {
    const bar = this.$('[data-bar]');
    const wrap = this.$('[data-cta-wrap]');
    if (!bar || !wrap || !('IntersectionObserver' in window)) return;
    // Show once the main CTA has scrolled up out of view.
    new IntersectionObserver(([e]) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('[data-cta]').tabIndex = show ? 0 : -1;
    }).observe(wrap);
  }

  /* ---------- product rows ---------- */
  card(p, { stars = false } = {}) {
    const a = document.createElement('a');
    a.className = 'c365-pcard';
    a.href = p.url;
    const img = document.createElement('div');
    img.className = 'c365-pcard__img';
    const src = p.featured_image || (p.images && p.images[0]);
    if (src) {
      const i = document.createElement('img');
      const url = typeof src === 'string' ? src : src.src || src.url;
      i.src = url + (url.includes('?') ? '&' : '?') + 'width=600';
      i.alt = p.title;
      i.loading = 'lazy';
      img.append(i);
    }
    a.append(img);
    if (stars) {
      const s = document.createElement('span');
      s.className = 'c365-pcard__stars';
      s.textContent = '★★★★★';
      a.append(s);
    }
    const t = document.createElement('span');
    t.className = 'c365-pcard__title';
    t.textContent = p.title;
    const pr = document.createElement('span');
    pr.className = 'c365-pcard__price';
    const min = p.price_min ?? p.price;
    const varies = p.price_varies ?? (p.price_max != null && p.price_max !== min);
    pr.textContent = varies ? T('product.from_price', 'From {price}', { price: this.money(min) }) : this.money(min);
    a.append(t, pr);
    return a;
  }

  async loadRelated() {
    const wrap = this.$('[data-related]');
    const grid = this.$('[data-related-grid]');
    try {
      const url = `${this.dataset.rootUrl.replace(/\/$/, '')}/recommendations/products.json?product_id=${this.dataset.productId}&limit=4`;
      const { products } = await fetch(url).then((r) => r.json());
      if (!products?.length) return;
      const stars = grid.dataset.stars === 'true';
      grid.replaceChildren(...products.map((p) => this.card(p, { stars })));
      wrap.hidden = false;
    } catch {
      /* no recommendations */
    }
  }

  async trackRecent() {
    const handle = this.dataset.productHandle;
    const list = [handle, ...readRecent().filter((h) => h !== handle)].slice(0, RECENT_MAX);
    writeRecent(list);

    const wrap = this.$('[data-recent]');
    const grid = this.$('[data-recent-grid]');
    const root = this.dataset.rootUrl.replace(/\/$/, '');
    const products = (
      await Promise.all(
        list.map((h) =>
          fetch(`${root}/products/${h}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);
    if (!products.length) return;
    grid.replaceChildren(...products.map((p) => this.card(p)));
    wrap.hidden = false;
    this.$('[data-recent-clear]').addEventListener('click', (e) => {
      e.preventDefault();
      writeRecent([]);
      wrap.hidden = true;
    });
  }
}

if (!customElements.get('c365-product')) customElements.define('c365-product', C365Product);
